import React, { useContext, useState } from 'react';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogTitle,
  Divider,
  IconButton,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';

import { useRefreshCall } from '../../Common/Queries/organizationQueries';

import { decodeJwt } from '../../utils';

import { AuthContext, DASHBOARD_ENDPOINT, LOGGED_IN_USER_INFO } from '../../constants';

import {
  getParsedValueFromLocalStorage,
  setValueToLocalStorage,
} from '../../services/localStorage';

const DialogDiv = styled('div')(({ theme }) => ({
  minWidth: '25rem',
  minHeight: '30rem',
  maxHeight: '30rem',
  [theme.breakpoints.down('sm')]: {
    minWidth: '16rem',
    minHeight: '26rem',
  },
  overflow: 'auto',
}));

const StyledDiv = styled('div')(() => ({
  overflow: 'hidden',
}));

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  paddingBottom: theme.spacing(2),
  paddingTop: theme.spacing(2),
  color: theme.palette.text.primary,
  '&.Mui-selected, &.Mui-selected:hover': {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.text.primary,
  },
  '&:hover': {
    backgroundColor: theme.palette.background.dragBtnHover,
  },
}));

const HeaderDiv = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const StyledListItemText = styled(ListItemText)(() => ({
  paddingLeft: '9px',
}));

export default function OrganisationModal({ modalState, handleCloseOrgModal, tenantsData }) {
  const { setUserInfo } = useContext(AuthContext);
  const storedUserInfos = getParsedValueFromLocalStorage(LOGGED_IN_USER_INFO);
  const claim = decodeJwt(storedUserInfos?.accessToken);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTenants, setCurrentTenant] = useState({
    name: claim['tenant-name'],
    id: claim['tenant-id'],
  });

  const refreshCall = useRefreshCall({
    onSuccess: () => {
      const storedUserInfo = getParsedValueFromLocalStorage(LOGGED_IN_USER_INFO);
      const claims = decodeJwt(storedUserInfo?.accessToken);
      const currentTenant = { name: claims['tenant-name'], id: claims['tenant-id'] };
      const tenantsArray = Array.isArray(storedUserInfo?.tenants)
        ? storedUserInfo.tenants.filter((tenant) => tenant?.name !== currentTenant?.name)
        : [];
      tenantsArray.unshift(currentTenant);
      const updatedUserInfo = {
        ...claims,
        accessToken: storedUserInfo?.accessToken,
        refreshToken: storedUserInfo?.refreshToken,
        tenants: tenantsArray,
      };
      setUserInfo(updatedUserInfo);
      setCurrentTenant(currentTenant);
      storedUserInfo.tenants = tenantsArray;
      setValueToLocalStorage(LOGGED_IN_USER_INFO, JSON.stringify(storedUserInfo));
      handleCloseOrgModal();
      window.location.href = DASHBOARD_ENDPOINT;
    },
    onError: (error) => {
      console.error('Error during refresh call:', error);
    },
  });

  const setOrganisation = async (tenant) => {
    setCurrentTenant(tenant);
    try {
      const payload = {
        tenantID: tenant.id,
        refreshToken: tenantsData?.refreshToken,
      };
      await refreshCall.mutateAsync(payload);
    } catch (error) {
      console.error('Error in setOrganisation:', error);
    }
  };

  const filteredTenants = tenantsData?.tenants?.filter((tenant) =>
    tenant.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog
      onClose={handleCloseOrgModal}
      aria-labelledby="customized-dialog-title"
      open={modalState}
    >
      <StyledDiv>
        <DialogTitle disableTypography>
          <HeaderDiv>
            <Typography variant="h3">Choose Organisation</Typography>
            <IconButton aria-label="close" onClick={handleCloseOrgModal}>
              <CloseIcon />
            </IconButton>
          </HeaderDiv>
        </DialogTitle>
        {tenantsData?.tenants?.length >= 8 && (
          <div style={{ padding: '12px' }}>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Search organisation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        )}

        <Divider />
        <DialogDiv>
          {filteredTenants?.length > 0 ? (
            filteredTenants.map((tenant) => {
              const isCurrentTenant = tenant?.id === currentTenants?.id;
              return (
                <div key={tenant.id}>
                  <StyledListItemButton
                    selected={isCurrentTenant}
                    onClick={() => setOrganisation(tenant)}
                  >
                    <StyledListItemText
                      primary={`${tenant.name}  ${isCurrentTenant ? '  (Current)' : ''}`}
                    />
                  </StyledListItemButton>
                  <Divider />
                </div>
              );
            })
          ) : (
            <Typography variant="body2" className="text-center" style={{ padding: '16px' }}>
              No organisations found
            </Typography>
          )}
        </DialogDiv>
      </StyledDiv>
    </Dialog>
  );
}
